import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiCode, FiDatabase, FiLayers, FiBarChart2, FiCloud, FiShield, FiRocket, FiCheck, FiArrowRight } from 'react-icons/fi';

const AIDevelopmentPage = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const services = [
        {
            icon: <FiCpu />,
            title: "Machine Learning",
            description: "Custom ML models tailored to your specific business needs and data patterns.",
            features: ["Predictive Analytics", "Natural Language Processing", "Computer Vision", "Recommendation Systems"]
        },
        {
            icon: <FiCode />,
            title: "AI Integration",
            description: "Seamlessly integrate AI capabilities into your existing systems and workflows.",
            features: ["API Development", "Legacy System Modernization", "Cloud AI Services", "Custom Integrations"]
        },
        {
            icon: <FiDatabase />,
            title: "Data Engineering",
            description: "Transform raw data into structured, AI-ready datasets for optimal model performance.",
            features: ["Data Pipeline Development", "ETL Processes", "Data Warehousing", "Data Quality Assurance"]
        },
        {
            icon: <FiLayers />,
            title: "Computer Vision",
            description: "Implement visual recognition systems that can interpret and understand images.",
            features: ["Object Detection", "Facial Recognition", "Image Classification", "Video Analytics"]
        },
        {
            icon: <FiBarChart2 />,
            title: "Predictive Analytics",
            description: "Leverage historical data to forecast trends, behaviors, and outcomes.",
            features: ["Time Series Forecasting", "Risk Assessment", "Customer Behavior Prediction", "Market Analysis"]
        },
        {
            icon: <FiCloud />,
            title: "AI Infrastructure",
            description: "Build scalable, secure infrastructure to support your AI initiatives.",
            features: ["Cloud Deployment", "GPU Clustering", "Model Serving", "Monitoring & Maintenance"]
        }
    ];

    const processSteps = [
        {
            title: "Discovery & Strategy",
            description: "We analyze your business needs and define AI objectives and success metrics."
        },
        {
            title: "Data Preparation",
            description: "We collect, clean, and structure your data for optimal AI model performance."
        },
        {
            title: "Model Development",
            description: "Our experts design, train, and validate custom AI models for your specific use case."
        },
        {
            title: "Integration & Deployment",
            description: "We seamlessly integrate the AI solution into your existing systems and workflows."
        },
        {
            title: "Optimization & Scaling",
            description: "We continuously monitor and refine the AI system to ensure peak performance."
        }
    ];

    const caseStudies = [
        {
            title: "Predictive Maintenance System",
            client: "Manufacturing Corp",
            result: "Reduced downtime by 45%",
            image: "https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Intelligent Customer Support",
            client: "E-Commerce Platform",
            result: "Improved resolution time by 60%",
            image: "https://images.unsplash.com/photo-1581291518633-83b4ebd1d83e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
        },
        {
            title: "Personalized Recommendation Engine",
            client: "Media Streaming Service",
            result: "Increased engagement by 35%",
            image: "https://images.unsplash.com/photo-1574375927938-d5a98e8ffe85?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 to-purple-900/20">
                    {/* Animated background elements */}
                    {[...Array(10)].map((_, i) => (
                        <motion.div
                            key={i}
                            className="absolute rounded-full bg-blue-500/10"
                            animate={{
                                scale: [1, 1.5, 1],
                                opacity: [0.1, 0.3, 0.1],
                            }}
                            transition={{
                                duration: 5 + Math.random() * 10,
                                repeat: Infinity,
                                delay: Math.random() * 5,
                            }}
                            style={{
                                width: Math.random() * 200 + 50,
                                height: Math.random() * 200 + 50,
                                top: `${Math.random() * 100}%`,
                                left: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>

                <div className="md:container mx-auto px-6 relative z-10">
                    <div className="flex flex-col lg:flex-row items-center justify-between">
                        <motion.div
                            className="lg:w-1/2 mb-12 lg:mb-0"
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8 }}
                        >
                            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                                Transform Your Business with <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI Solutions</span>
                            </h1>
                            <p className="text-xl text-gray-300 mb-8">
                                We build intelligent systems that learn, adapt, and deliver measurable results for your business.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a
                                    href="/quickenquiry">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-lg flex items-center justify-center"
                                    >
                                        Start Your AI Journey
                                        <FiArrowRight className="ml-2" />
                                    </motion.button>
                                </a>
                                {/* <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="border border-gray-700 hover:border-gray-600 text-white font-bold py-4 px-8 rounded-lg"
                                >
                                    View Case Studies
                                </motion.button> */}
                            </div>
                        </motion.div>

                        <motion.div
                            className="lg:w-1/2"
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl blur-xl opacity-30 animate-pulse"></div>
                                <div className="relative bg-gray-800/50 backdrop-blur-md border border-gray-700 rounded-2xl p-8">
                                    <div className="grid grid-cols-2 gap-4">
                                        {[1, 2, 3, 4].map((item) => (
                                            <motion.div
                                                key={item}
                                                className="bg-gray-900/50 p-4 rounded-xl border border-gray-700"
                                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                            >
                                                <div className="h-4 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mb-3 w-3/4"></div>
                                                <div className="h-3 bg-gray-700 rounded-full mb-2"></div>
                                                <div className="h-3 bg-gray-700 rounded-full w-5/6"></div>
                                            </motion.div>
                                        ))}
                                    </div>
                                    <div className="mt-6 p-4 bg-gray-900/30 rounded-xl border border-gray-700">
                                        <div className="flex items-center mb-3">
                                            <div className="h-8 w-8 bg-blue-500 rounded-full mr-3"></div>
                                            <div>
                                                <div className="h-3 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full w-32 mb-2"></div>
                                                <div className="h-2 bg-gray-700 rounded-full w-24"></div>
                                            </div>
                                        </div>
                                        <div className="h-3 bg-gray-700 rounded-full mb-2"></div>
                                        <div className="h-3 bg-gray-700 rounded-full w-5/6"></div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div >
            </section >

            {/* Services Section */}
            < section className="py-20 relative" >
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI Services</span>
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            We offer end-to-end AI development services to help you harness the power of artificial intelligence.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-700 hover:border-blue-500/30 transition-all"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -10 }}
                            >
                                <div className="text-blue-400 text-3xl mb-4">{service.icon}</div>
                                <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                                <p className="text-gray-400 mb-4">{service.description}</p>
                                <ul className="space-y-2">
                                    {service.features.map((feature, i) => (
                                        <li key={i} className="flex items-center">
                                            <FiCheck className="text-green-400 mr-2" />
                                            <span className="text-gray-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section >

            {/* Process Section */}
            < section className="py-20 bg-gradient-to-r from-gray-900 to-black relative" >
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">AI Development Process</span>
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            A structured approach to delivering AI solutions that drive real business value.
                        </p>
                    </motion.div>

                    <div className="relative">
                        <div className="absolute left-8 lg:left-1/2 lg:-translate-x-1/2 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-purple-500"></div>

                        <div className="space-y-12">
                            {processSteps.map((step, index) => (
                                <motion.div
                                    key={index}
                                    className="relative pl-20 lg:pl-0 lg:flex lg:items-center lg:even:flex-row-reverse"
                                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                >
                                    <div className="lg:w-1/2 lg:px-12">
                                        <div className="absolute left-8 lg:left-1/2 lg:-translate-x-1/2 top-0 flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full text-white text-xl font-bold">
                                            {index + 1}
                                        </div>
                                        <div className="lg:p-8 lg:rounded-2xl lg:bg-gray-800/30 lg:backdrop-blur-sm lg:border lg:border-gray-700">
                                            <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                                            <p className="text-gray-400">{step.description}</p>
                                        </div>
                                    </div>
                                    <div className="lg:w-1/2 hidden lg:block"></div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </div>
            </section >

            {/* Case Studies Section */}
            < section className="py-20 relative" >
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="text-center mb-16"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold mb-6">
                            AI <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Success Stories</span>
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            See how our AI solutions have delivered measurable results for businesses across industries.
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {caseStudies.map((study, index) => (
                            <motion.div
                                key={index}
                                className="bg-gradient-to-b from-gray-800/50 to-gray-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700 hover:border-blue-500/30 transition-all"
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                                whileHover={{ y: -10 }}
                            >
                                <div className="h-48 overflow-hidden">
                                    <img
                                        src={study.image}
                                        alt={study.title}
                                        className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="text-2xl font-bold mb-2">{study.title}</h3>
                                    <p className="text-gray-400 mb-4">{study.client}</p>
                                    <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-4 rounded-lg border border-gray-700">
                                        <p className="text-lg font-semibold text-blue-400">{study.result}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section >

            {/* CTA Section */}
            < section className="py-20 relative" >
                <div className="md:container mx-auto px-6">
                    <motion.div
                        className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-3xl p-8 md:p-12 text-center border border-gray-700"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">
                            Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Transform</span> Your Business with AI?
                        </h2>
                        <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
                            Let's discuss how our AI development services can help you achieve your business goals.
                        </p>
                        <a
                            href="/quickenquiry"

                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-lg flex items-center justify-center mx-auto"
                            >
                                Schedule a Consultation
                                <FiArrowRight className="ml-2" />
                            </motion.button>
                        </a>

                    </motion.div>
                </div>
            </section >
        </div >
    );
};

export default AIDevelopmentPage;